import {
  evaluateTokenNodes,
  getExtendedTokens,
} from '@skop-app/lib-formula-core/preview'
import { type TokenNode, TokenType } from '@skop-app/lib-formula-core'
import type {
  FormulaConfig,
  FormulaConfigField,
  FormulaEvaluationEntry,
  FormulaEvaluationResult,
  FormulaEvaluationStatus,
} from '@/types'

import { isMissingFormulaToken, RESULT_REFERENCE_NAME } from './formula.config'

type EvaluateFormulaConfigParams = {
  config: FormulaConfig
  resolveReference: (referenceName: string) => string | undefined
  resolveFieldValue?: (field: FormulaConfigField) => string | undefined
}

type FormulaIssueSet = {
  invalidRefs: Set<string>
  missingRefs: Set<string>
}

const LEGACY_REFERENCE_FORMULA_PATTERN = /^[A-Za-z_][A-Za-z0-9_.]*$/
const LEGACY_BARE_REFERENCE_PATTERN = /^[A-Za-z_][A-Za-z0-9_.]*$/

export const FORMULA_DISPLAY_PLACEHOLDER = '-'

export const normalizeFormulaExpression = (formula?: string): string => {
  const trimmedFormula = formula?.trim() ?? ''
  if (!trimmedFormula) return ''
  if (LEGACY_REFERENCE_FORMULA_PATTERN.test(trimmedFormula)) {
    return `{${trimmedFormula}}`
  }
  return trimmedFormula
}

export const getFormulaDefaultValue = (
  formula?: string,
): string | undefined => {
  const rawFormula = formula?.trim() ?? ''
  if (!rawFormula) return undefined

  const normalizedFormula = normalizeFormulaExpression(rawFormula)
  if (!normalizedFormula) return undefined

  if (
    LEGACY_BARE_REFERENCE_PATTERN.test(rawFormula) &&
    !rawFormula.includes('{') &&
    !rawFormula.includes('}') &&
    !rawFormula.includes('(') &&
    !rawFormula.includes('"') &&
    !rawFormula.includes("'")
  ) {
    return undefined
  }

  return normalizedFormula
}

const createEvaluationEntry = (
  value: string,
  status: FormulaEvaluationStatus,
): FormulaEvaluationEntry => ({
  value,
  status,
})

const createMissingEntry = () =>
  createEvaluationEntry('', 'missing' satisfies FormulaEvaluationStatus)

const createEmptyIssueSet = (): FormulaIssueSet => ({
  invalidRefs: new Set<string>(),
  missingRefs: new Set<string>(),
})

export const isFormulaInvalidMarker = (value: unknown): boolean => {
  if (typeof value === 'number') return !Number.isFinite(value)
  if (value === null || value === undefined) return false

  const normalized = String(value).trim()
  return (
    normalized === 'NaN' ||
    normalized === 'Infinity' ||
    normalized === '-Infinity'
  )
}

export const toFormulaDisplayValue = (
  value: string,
  status: FormulaEvaluationStatus,
): string => {
  if (status !== 'valid') return FORMULA_DISPLAY_PLACEHOLDER
  return value === '' ? FORMULA_DISPLAY_PLACEHOLDER : value
}

const createResolvedReferenceEntry = (
  rawValue: string | undefined,
): FormulaEvaluationEntry => {
  if (
    rawValue === undefined ||
    rawValue === null ||
    rawValue === '' ||
    isMissingFormulaToken(rawValue)
  ) {
    return createMissingEntry()
  }

  const value = String(rawValue)
  if (isFormulaInvalidMarker(value)) {
    return createEvaluationEntry(value, 'invalid')
  }

  const entry = getExtendedTokens({ value })['value']
  if (!entry || entry.validationErrors.length > 0) {
    return createEvaluationEntry(value, 'valid')
  }

  const evaluatedValue = evaluateTokenNodes(entry.tokenNodes, () => '')
  if (isFormulaInvalidMarker(evaluatedValue)) {
    return createEvaluationEntry(evaluatedValue, 'invalid')
  }

  return createEvaluationEntry(evaluatedValue, 'valid')
}

const collectReferencedNames = (tokenNodes: TokenNode[]): string[] => {
  const names = new Set<string>()

  const visit = (nodes: TokenNode[]) => {
    nodes.forEach((node) => {
      if (node.type === TokenType.ReferenceName) {
        names.add(node.value)
      }
      if (node.innerNodes.length > 0) {
        visit(node.innerNodes)
      }
    })
  }

  visit(tokenNodes)
  return Array.from(names)
}

const buildFormulaError = (issues: FormulaIssueSet): string | undefined => {
  const invalidRefs = Array.from(issues.invalidRefs)
  const missingRefs = Array.from(issues.missingRefs)
  const messages = [
    invalidRefs.length > 0
      ? `Invalid values ${invalidRefs.join(', ')}`
      : undefined,
    missingRefs.length > 0
      ? `Missing values ${missingRefs.join(', ')}`
      : undefined,
  ].filter((message): message is string => Boolean(message))

  return messages.length > 0 ? messages.join('; ') : undefined
}

const getQuotedLiteral = (formula: string): string | undefined => {
  const trimmedFormula = formula.trim()
  const match = /^(['"])(.*)\1$/.exec(trimmedFormula)
  return match?.[2]
}

const isSelfReferentialDefault = (
  referenceName: string,
  defaultFormula: string,
): boolean => {
  const literal = getQuotedLiteral(defaultFormula)
  if (!literal) return false

  const [rootReferenceName] = referenceName.split('.')
  return literal === referenceName || literal === rootReferenceName
}

/** Editor preview only. Business calculations use formula.runtime. */
export const evaluateFormulaConfig = ({
  config,
  resolveReference,
  resolveFieldValue,
}: EvaluateFormulaConfigParams): FormulaEvaluationResult => {
  const resultReferenceName =
    config.formulaField.referenceName || RESULT_REFERENCE_NAME
  const resultReferenceLowerCase = resultReferenceName.toLowerCase()
  const supportedRefs = [
    ...config.fields
      .map((field) => field.referenceName?.trim())
      .filter((referenceName): referenceName is string =>
        Boolean(referenceName),
      ),
    resultReferenceName,
  ]

  const formulasByReference = config.fields.reduce<Record<string, string>>(
    (acc, field) => {
      const referenceName = field.referenceName?.trim()
      if (!referenceName) return acc

      const currentValue = resolveFieldValue?.(field)
      if (
        currentValue !== undefined &&
        currentValue !== null &&
        currentValue !== '' &&
        !isMissingFormulaToken(currentValue)
      ) {
        acc[referenceName] = currentValue
        return acc
      }

      const defaultFormula = getFormulaDefaultValue(field.formula)
      if (
        defaultFormula &&
        !isSelfReferentialDefault(referenceName, defaultFormula)
      ) {
        acc[referenceName] = defaultFormula
      }

      return acc
    },
    {
      [resultReferenceName]: normalizeFormulaExpression(
        config.formulaField.formula,
      ),
    },
  )
  const extendedEntries = getExtendedTokens(formulasByReference, supportedRefs)
  const orderedEntries = Object.values(extendedEntries).sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  )

  const entriesByLowerReference: Record<string, FormulaEvaluationEntry> = {}
  const entriesByRef: Record<string, FormulaEvaluationEntry> = {}
  const issuesByLowerReference: Record<string, FormulaIssueSet> = {}
  const externalReferenceCache = new Map<string, FormulaEvaluationEntry>()
  let hasInputValues = false

  const storeEntry = (
    referenceName: string,
    entry: FormulaEvaluationEntry,
    issues: FormulaIssueSet = createEmptyIssueSet(),
  ) => {
    const lowerReferenceName = referenceName.toLowerCase()
    entriesByLowerReference[lowerReferenceName] = entry
    entriesByRef[referenceName] = entry
    issuesByLowerReference[lowerReferenceName] = issues
  }

  const resolveDependencyEntry = (
    referenceName: string,
  ): FormulaEvaluationEntry => {
    const lowerReferenceName = referenceName.toLowerCase()
    const computedEntry = entriesByLowerReference[lowerReferenceName]
    if (computedEntry) return computedEntry

    const cachedEntry = externalReferenceCache.get(lowerReferenceName)
    if (cachedEntry) return cachedEntry

    const resolvedEntry = createResolvedReferenceEntry(
      resolveReference(referenceName),
    )
    externalReferenceCache.set(lowerReferenceName, resolvedEntry)
    if (!entriesByRef[referenceName]) {
      entriesByRef[referenceName] = resolvedEntry
    }
    return resolvedEntry
  }

  orderedEntries.forEach((entry) => {
    const directReferences = collectReferencedNames(entry.tokenNodes)
    const invalidRefs = new Set<string>()
    const missingRefs = new Set<string>()

    if (entry.validationErrors.length > 0) {
      invalidRefs.add(entry.referenceNameOrig)
    }

    const evaluatedValue = evaluateTokenNodes(entry.tokenNodes, (prop) => {
      const dependencyEntry = resolveDependencyEntry(prop)
      if (dependencyEntry.status === 'missing') {
        missingRefs.add(prop)
        return ''
      }
      if (dependencyEntry.status === 'invalid') {
        invalidRefs.add(prop)
        return ''
      }
      return dependencyEntry.value
    })

    if (
      isFormulaInvalidMarker(evaluatedValue) &&
      invalidRefs.size === 0 &&
      missingRefs.size === 0 &&
      directReferences.length > 0
    ) {
      directReferences.forEach((referenceName) => {
        const dependencyEntry = resolveDependencyEntry(referenceName)
        if (dependencyEntry.status === 'invalid') {
          invalidRefs.add(referenceName)
          return
        }

        if (
          dependencyEntry.status === 'valid' &&
          Number.isNaN(Number(dependencyEntry.value))
        ) {
          invalidRefs.add(referenceName)
        }
      })
    }

    const status: FormulaEvaluationStatus =
      invalidRefs.size > 0 || isFormulaInvalidMarker(evaluatedValue)
        ? 'invalid'
        : missingRefs.size > 0
          ? 'missing'
          : 'valid'

    const nextEntry =
      status === 'missing'
        ? createMissingEntry()
        : createEvaluationEntry(evaluatedValue, status)

    storeEntry(entry.referenceNameOrig, nextEntry, {
      invalidRefs,
      missingRefs,
    })

    hasInputValues = hasInputValues || nextEntry.status !== 'missing'
  })

  const resultEntry =
    entriesByLowerReference[resultReferenceLowerCase] ?? createMissingEntry()
  const resultIssues =
    issuesByLowerReference[resultReferenceLowerCase] ?? createEmptyIssueSet()

  return {
    value: resultEntry.status === 'valid' ? resultEntry.value : '',
    displayValue: toFormulaDisplayValue(resultEntry.value, resultEntry.status),
    status: resultEntry.status,
    error: buildFormulaError(resultIssues),
    hasInputValues,
    entriesByRef,
  }
}
